import { Inject, Injectable, LOCALE_ID } from "@angular/core";
import {
  EMPTY,
  lastValueFrom,
  Observable,
  of,
  ReplaySubject,
  throwError,
  timer,
} from "rxjs";
import { environment } from "src/environments/environment";
import { HttpHeaders, HttpClient, HttpErrorResponse } from "@angular/common/http";
import { AppUtils } from "../models/app-utils";
import {
  shareReplay,
  tap,
  map,
  catchError,
  retry,
  expand,
  reduce,
} from "rxjs/operators";
export type AuthStatus = "ok" | "no-token" | "invalid";
export type LoginResult = "ok" | "bad-credentials" | "error";

/** Token mémorisé entre deux chargements (connexion par identifiants ou saisie manuelle). */
const TOKEN_KEY = "authToken";

/** btoa() ne gère que le latin-1 : on encode d'abord en UTF-8 (mots de passe accentués). */
function toBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
}

@Injectable({
  providedIn: "root",
})
export class TralalereService {
  
  learners: Array<UserTrala>;
  accesstoken: string;
  tralalereUrl = "https://lms-mathia.api.tralalere.com";
  postTralalereUrl = this.tralalereUrl + "/api";
  user: UserTrala;
  userReady = new ReplaySubject<void>();
  learnersReady = new ReplaySubject<void>();
	classes: any;
  workGroups: any[] = [];
  assignations: Array<any>;
  /** Résultat mémorisé de ensureAuth() : l'authentification n'est jouée qu'une fois par chargement. */
  private authPromise?: Promise<AuthStatus>;

  constructor(
    private http: HttpClient,
    @Inject(LOCALE_ID) public locale: string,
  ) {
    
  }

  /**
   * Récupère le token puis valide la session auprès de l'API, une seule fois par
   * chargement de l'appli (le résultat est mémorisé et partagé par le guard de routes).
   * - "ok" : session valide, `user` est renseigné.
   * - "no-token" : aucun accesstoken fourni -> page de login.
   * - "invalid" : token refusé par l'API -> page unauthorized.
   */
  ensureAuth(): Promise<AuthStatus> {
    if (!this.authPromise) {
      this.authPromise = this.resolveAuth();
    }
    return this.authPromise;
  }

  private async resolveAuth(): Promise<AuthStatus> {
    const fromUrl = AppUtils.getUrlParams().accesstoken;
    const token = fromUrl || this.storedToken();
    if (!token) {
      return "no-token";
    }
    this.accesstoken = token;
    try {
      await this.init();
      return "ok";
    } catch {
      if (fromUrl) {
        return "invalid";
      }
      // token mémorisé expiré ou révoqué : on repasse par le formulaire de login
      // plutôt que par la page unauthorized, l'utilisateur peut se reconnecter.
      this.clearAuth();
      return "no-token";
    }
  }

  /**
   * Token conservé d'un chargement à l'autre : celui obtenu par le formulaire de login
   * (ou collé à la main hors production), sinon `environment.debugToken` hors production.
   */
  private storedToken(): string | undefined {
    const stored = this.readStoredToken();
    if (stored) {
      return stored;
    }
    return environment.production ? undefined : environment.debugToken || undefined;
  }

  private readStoredToken(): string | undefined {
    try {
      return localStorage.getItem(TOKEN_KEY) || undefined;
    } catch {
      // stockage indisponible (navigation privée restreinte, iframe cloisonnée)
      return undefined;
    }
  }

  /**
   * Connexion par identifiants : le LMS (Drupal) délivre un JWT sur /jwt/token en
   * authentification HTTP Basic. Le token obtenu est mémorisé pour les chargements suivants.
   */
  async login(username: string, password: string): Promise<LoginResult> {
    let token: string | undefined;
    try {
      const res = await lastValueFrom(
        this.http.get<{ token?: string; access_token?: string }>(
          this.tralalereUrl + "/jwt/token",
          {
            headers: new HttpHeaders({
              Authorization: "Basic " + toBase64(`${username}:${password}`),
            }),
          },
        ),
      );
      token = res?.token || res?.access_token;
    } catch (err) {
      return this.isAuthFailure(err) ? "bad-credentials" : "error";
    }
    if (!token) {
      return "error";
    }
    this.setToken(token);
    // le JWT est valide mais la session peut être refusée par l'API métier (compte sans accès)
    return (await this.ensureAuth()) === "ok" ? "ok" : "error";
  }

  /** Mémorise un token (login ou saisie manuelle) et force un nouveau ensureAuth(). */
  setToken(token: string) {
    const value = token.trim();
    try {
      localStorage.setItem(TOKEN_KEY, value);
    } catch {
      // pas de mémorisation possible : la session vaudra pour ce chargement seulement
    }
    this.accesstoken = value;
    this.authPromise = undefined;
  }

  /** Déconnexion : purge token + cache local (bouton de dev, token refusé, logout). */
  clearAuth() {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // rien à purger si le stockage est indisponible
    }
    this.accesstoken = "";
    this.user = undefined as any;
    this.authPromise = undefined;
    this.cleanOldData();
  }

  init() {
    return new Promise<void>((resolve, reject) => {
      const currentUserToken = localStorage.getItem("accesstoken");
      const sameToken = currentUserToken === this.accesstoken;
      if (!sameToken) {
        this.cleanOldData();
      } else {
        // charge le cache local en attendant la réponse fraîche du serveur,
        // pour rester à jour (ex: une classe ajoutée au compte depuis la dernière mise en cache).
        this.user = JSON.parse(localStorage.getItem("user")!);
        const learners = localStorage.getItem("learners");
        if (learners) {
          const l = JSON.parse(learners);
          if (Array.isArray(l)) {
            this.learners = l;
          }
        }
        const classes = localStorage.getItem("classes");
        if (classes) {
          const c = JSON.parse(classes);
          if (Array.isArray(c)) {
            this.classes = c;
          }
        }
      }
      this.http
        .get<UsersTrala>(this.postTralalereUrl + "/users/me")
        // un token refusé le restera : on ne réessaie que les erreurs réseau/serveur.
        .pipe(
          retry({
            count: 3,
            delay: (err) =>
              this.isAuthFailure(err) ? throwError(() => err) : timer(500),
          }),
        )
        .subscribe({
          next: (result) => {
            if (result?.data && result.data[0]) {
              this.user = result.data[0];
              localStorage.setItem("accesstoken", this.accesstoken);
              localStorage.setItem("user", JSON.stringify(this.user));
              this.userReady.next();
              resolve();
            } else if (sameToken && this.user) {
              this.userReady.next();
              resolve();
            } else {
              reject();
            }
          },
          error: (err) => {
            if (this.isAuthFailure(err)) {
              // token expiré ou révoqué : on ne se rabat pas sur le cache local,
              // sinon l'appli semble connectée mais tous les appels échouent.
              this.user = undefined as any;
              this.cleanOldData();
              reject(err);
            } else if (sameToken && this.user) {
              // API injoignable : on repart du cache pour ne pas bloquer l'utilisateur.
              this.userReady.next();
              resolve();
            } else {
              reject(err);
            }
          },
        });
    });
  }

  private isAuthFailure(err: any): boolean {
    const status = err instanceof HttpErrorResponse ? err.status : err?.status;
    return status === 401 || status === 403;
  }

  cleanOldData() {
    ["accesstoken", "user", "learners", "classes"].forEach((key) => {
      localStorage.removeItem(key);
    });
  }
  getLearners(): any {
    return new Promise<void>((resolve) => {
      this.userReady.subscribe(() => {
        if (this.learners) {
          this.learnersReady.next();
          resolve();
        }
        // no else because we always want to be up to date, but without waiting if learners already available.
        this.http
          .get<UsersTrala>(this.postTralalereUrl + "/learners")
          .subscribe({
            next: async (result: any) => {
              if (result?.data && result.data[0]) {
                this.learners = result.data;
                let cur = result;
                while (cur.next) {
                  await new Promise<void>(r => {
                    this.http.get<UsersTrala>(cur.next).subscribe({
                      next: (rn: any) => { cur = rn; this.learners = this.learners.concat(rn.data); r(); },
                      error: () => r(),
                    });
                  });
                }
                localStorage.setItem("learners", JSON.stringify(this.learners));
                this.learnersReady.next();
                resolve();
              } else {
                console.log("no learners");
                this.learners = this.learners || [];
                resolve();
              }
            },
            error: (err) => {
              console.error('getLearners error:', err);
              this.learners = this.learners || [];
              resolve();
            },
          });
      });
    });
  }

  getAssignations(fromTimestamp?: number, toTimestamp?: number){
    return new Promise<void>(async (resolve) => {
      // L'API filtre sur la date de création de l'assignation, alors que le reste de
      // l'appli (affichage, filtres de dates) se base sur sa date de début programmée
      // (dates.start). Une assignation peut être créée avant (préparée à l'avance) ou
      // après (corrigée a posteriori) sa date de début : on élargit donc la fenêtre
      // demandée au serveur d'une marge de sécurité, et on laisse le filtrage exact sur
      // la période être fait côté client via dateRaw (voir applyFilters()).
      const CREATION_BUFFER_SECONDS = 90 * 24 * 3600;
      const from = fromTimestamp ?? (Math.floor(Date.now() / 1000) - 30 * 24 * 3600);
      const fromDate = new Date((from - CREATION_BUFFER_SECONDS) * 1000).toISOString().split('T')[0];
      let url = `${this.postTralalereUrl}/assignations-list?created_after=${fromDate}`;
      if (toTimestamp) {
        const toDate = new Date((toTimestamp + CREATION_BUFFER_SECONDS) * 1000).toISOString().split('T')[0];
        url += `&created_before=${toDate}`;
      }
      this.assignations = [];
      this.http
          .get<UsersTrala>(url)
          .subscribe({
            next: async (result: any) => {
              if (result?.data && result.data.length > 0) {
                this.assignations = result.data;
                let cur = result;
                while(cur.next){
                  await new Promise<void>(r => {
                    this.http.get<UsersTrala>(cur.next).subscribe({
                      next: (rn: any) => { cur = rn; this.assignations = this.assignations.concat(rn.data); r(); },
                      error: () => r(),
                    });
                  });
                }
              } else {
                this.assignations = this.assignations || [];
              }
              resolve();
            },
            error: () => {
              this.assignations = this.assignations || [];
              resolve();
            },
          });
    });
  }
  getAllStudentsIds() {
    return this.learners.map((s) => s.id);
  }

  getClasses() {
    return new Promise<Array<any>>(resolve => {
      // toujours interroger le serveur pour rester à jour (le cache localStorage
      // peut être obsolète, ex: une classe créée après la dernière mise en cache).
      const groupIds = [...new Set(this.user.groups)];
      this.http.get(this.postTralalereUrl + "/groups-detail/" + groupIds.join(',')).subscribe({
        next: async (res: any) => {
          if (res.data) {
            let allData = res.data;
            let cur = res;
            while (cur.next) {
              await new Promise<void>(r => {
                this.http.get<any>(cur.next).subscribe({
                  next: (rn: any) => { cur = rn; allData = allData.concat(rn.data); r(); },
                  error: () => r(),
                });
              });
            }
            // Le serveur peut renvoyer plusieurs fois le même groupe (ex: identifiant
            // dupliqué dans user.groups) : on déduplique par id pour éviter que chaque
            // élève de ce groupe soit compté/traité deux fois (recommandations, etc.).
            allData = [...new Map(allData.map((c: any) => [c.id ?? c.code, c])).values()];
            this.classes = allData.filter((c: any) => c.type === "2" && !c.archived);
            this.workGroups = allData.filter((c: any) => c.type !== "2" && !c.archived);
            localStorage.setItem("classes", JSON.stringify(this.classes));
          } else {
            this.classes = this.classes || [];
          }
          resolve(this.classes);
        },
        error: () => {
          console.error("getClasses error");
          this.classes = this.classes || [];
          resolve(this.classes);
        }
      });
    });
  }

  updateActivityStatusResponse(a: any, newState: never) {
    return new Promise<void>(resolve => {
      
        this.http.patch(this.postTralalereUrl + "/assignations/" + a.id, {"state_term": newState}).subscribe({next: (res: any) => {
          console.log(res);
          resolve();
        }, error: (err) => {
          // OK api not ready yet
          console.log(err);
          resolve();
        }})
      
    })
  }

  createAssignation(contentTitle: string, contentJson: string, learnerUids: string[]) {
    const promises = learnerUids.map(uid =>
      lastValueFrom(
        this.http.post(this.postTralalereUrl + '/assignations', {
          activity_title: contentTitle,
          activity_content: contentJson,
          learner_uid: uid,
        }).pipe(catchError(() => of(null)))
      )
    );
    return Promise.all(promises);
  }

  deleteAssignation(assignations: any) {
    return new Promise<boolean>(async resolve => {
      const promises: Array<Promise<void>> = [];
      for (const assignation of assignations) {
        for (const a of assignation.activityLinkUsers) {
          await new Promise<void>(resolve => {
            this.http.delete(this.postTralalereUrl + "/assignations/" + a.id).subscribe({
              next: (res: any) => {
                console.log(res);
                resolve();
              }, error: (err) => {
                // OK api not ready yet
                console.log(err);
                resolve();
              }
            })
          });
        }
      }
      resolve(true);
    })
  }

}

interface UsersTrala {
  data: UserTrala[];
  self: Self;
}

interface UserTrala {
  id: string;
  label: string;
  self: string;
  status: string;
  firstName: any;
  lastName: any;
  role: number[];
  roleCallback: number[];
  email: string;
  you_are: string;
  type: any;
  contact_email: string;
  institution: any;
  expirePassword: string;
  find_us: string;
  region: any;
  email_status: boolean;
  newsletter: boolean;
  updateProfile: boolean;
  sso: boolean;
  sso_token: any;
  picture: string;
  access: string;
  level: any;
  codeid: string;
  groups: string[];
  parent: string;
  created: string;
  updateToAcademicMail: boolean;
}

interface Self {
  title: string;
  href: string;
}
